import { Button, Checkbox, Form, Modal, Select } from 'antd';
import { useMemoizedFn, useToggle } from 'ahooks';
import {
  ConversationFilterAdvance,
  useFilterOptions,
  useFilterToBeCheckedEnable,
  useManualCloseEnable,
} from 'hooks/business/conversationsHooks/useFilterAdvance';
import { useTranslate } from '@tolgee/react';
import { Ref, forwardRef, useMemo, useImperativeHandle, useEffect } from 'react';
import { useReceptionConfig } from 'views/Setting/ConversationSetting/useReceptionConfig';
import useIsAdminViewWithMultiAssist from 'hooks/business/useIsAdminViewWithMultiAssist';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import { AssistantFromSameDeptData } from 'services/api/admin';
import { useHasMercadoChatPermission } from 'hooks/business/im/useMercadoChatPermission';
import { uniq } from 'lodash-es';
import { useOnlyMercadoPlatform } from 'hooks/business/im/useOnlyMercadoPlatform';
import { useAtomValue } from 'jotai';
import { newPermissionState } from 'hooks/business/useNewPermisson';
import { getStoreCount } from 'components/StoreScopes';

interface CustomSelectProps {
  value?: string[];
  onChange?: (value: string[] | undefined) => void;
}

const MERCADO_STATUS_LIST = ['REPUTATION', 'MEDIATION'];

const ConversationStatusSelect = (props: CustomSelectProps) => {
  const { value, onChange } = props;
  const { t } = useTranslate();
  const { toBeCheckedFilter } = useFilterOptions();
  const { isDisplayTobeCheckedConversation = false } = useReceptionConfig() || {};
  const isAdminViewWithMultiAssist = useIsAdminViewWithMultiAssist();
  const {
    whiteListConfig: { UNPROCESSED_WITH_BOT_REPLIED = false, SIMPLUS_CONV_STATUS = false } = {},
  } = useOrgConfigStateValue();
  const toBeCheckedEnable = useFilterToBeCheckedEnable();

  const [selected] = useMemo(() => {
    return (value ?? [])?.filter((item) => !MERCADO_STATUS_LIST.includes(item));
  }, [value]);

  const manualCloseEnable = useManualCloseEnable();

  // 会话状态
  const statusOptions = useMemo(
    () =>
      SIMPLUS_CONV_STATUS
        ? [
            {
              label: isAdminViewWithMultiAssist
                ? t('filter.replied')
                : `${t('filter.replied')}(${t('header.assign.to.me')})`,
              value: 'UNPROCESSED',
            },
            {
              label: isAdminViewWithMultiAssist
                ? t('未关闭')
                : `${t('未关闭')}(${t('header.assign.to.me')})`,
              value: 'NOT_CLOSE',
            },
            {
              label: isAdminViewWithMultiAssist
                ? t('to_be_checked')
                : `${t('to_be_checked')}(${t('header.assign.to.me')})`,
              value: 'BOT_REPLIED',
            },
            ...(isAdminViewWithMultiAssist
              ? [
                  {
                    label: t('人工关闭'),
                    value: 'MANUAL_CLOSE',
                  },
                ]
              : []),
            ...(isAdminViewWithMultiAssist
              ? [
                  {
                    label: isAdminViewWithMultiAssist
                      ? t('filter.unassigned')
                      : `${t('filter.unassigned')}(${t('header.assign.to.me')})`,
                    value: 'WAIT_ASSIGN',
                  },
                ]
              : []),
          ]
        : [
            {
              label: `${t('inreception')}${
                isAdminViewWithMultiAssist ? '' : `(${t('header.assign.to.me')})`
              }`,
              value: 'IN_RECEPTION',
            },
            {
              label: isAdminViewWithMultiAssist
                ? t('filter.replied')
                : `${t('filter.replied')}(${t('header.assign.to.me')})`,
              value: 'UNPROCESSED',
            },
            ...(toBeCheckedEnable
              ? [
                  {
                    label: t('to_be_checked'),
                    value: 'BOT_REPLIED',
                  },
                ]
              : []),
            {
              label: t('filter.unread'),
              value: 'UNREAD',
            },
            ...(manualCloseEnable
              ? [
                  {
                    label: t('人工关闭'),
                    value: 'MANUAL_CLOSE',
                  },
                ]
              : []),
            ...(isAdminViewWithMultiAssist
              ? [
                  {
                    label: t('filter.unassigned'),
                    value: 'WAIT_ASSIGN',
                  },
                ]
              : []),
          ],
    [t, toBeCheckedEnable, isAdminViewWithMultiAssist, manualCloseEnable, SIMPLUS_CONV_STATUS],
  );

  const wrapOnChange = useMemoizedFn((values: string[]) => {
    const excludes = (value ?? []).filter((item) => MERCADO_STATUS_LIST.includes(item));
    onChange?.(uniq([...excludes, ...(values ?? [])]));
  });

  const handleSelectChange = useMemoizedFn((values) => {
    if (!UNPROCESSED_WITH_BOT_REPLIED) {
      // 特殊处理: 给 api 的参数有两个
      if (isDisplayTobeCheckedConversation) {
        wrapOnChange(['UNPROCESSED', 'BOT_REPLIED']);
        return;
      }
    }
    wrapOnChange(values ? [values] : []);
  });

  return (
    <div>
      <Select
        options={statusOptions}
        value={selected}
        onChange={handleSelectChange}
        allowClear
        placeholder={t('WhatsApp.请选择')}
      />
      {!SIMPLUS_CONV_STATUS && UNPROCESSED_WITH_BOT_REPLIED && value?.includes('UNPROCESSED') ? (
        <div className="mt-2 px-4 py-2 bg-fill-200">
          <Checkbox.Group value={value} onChange={wrapOnChange as any}>
            {toBeCheckedFilter.map((value) => (
              <Checkbox key={value.value} value={value.value}>
                {value.name}
              </Checkbox>
            ))}
          </Checkbox.Group>
        </div>
      ) : null}
    </div>
  );
};

const ConversationTagSelect = (props: CustomSelectProps) => {
  const { value, onChange } = props;
  const { t } = useTranslate();
  const selected = useMemo(
    () => (value ?? [])?.filter((item) => MERCADO_STATUS_LIST.includes(item)),
    [value],
  );

  const options = useMemo(
    () => [
      {
        label: t('影响声誉'),
        value: 'REPUTATION',
      },
      {
        label: t('调解中'),
        value: 'MEDIATION',
      },
    ],
    [t],
  );
  const handleSelectChange = useMemoizedFn((values: string[]) => {
    const excludes = (value ?? [])?.filter((item) => !MERCADO_STATUS_LIST.includes(item));
    onChange?.(uniq([...excludes, ...values]));
  });

  return (
    <Select
      options={options}
      value={selected}
      onChange={handleSelectChange}
      allowClear
      placeholder={t('WhatsApp.请选择')}
      mode="multiple"
    />
  );
};

export function useShowShopeeAffiliate() {
  const { roles } = useAtomValue(newPermissionState);
  const showShopeeAffiliate = useMemo(
    () =>
      roles.some(({ assignConversationConfig: { platformStoreScopes } }) =>
        getStoreCount(platformStoreScopes.filter((_) => _.platform === 'SHOPEE')),
      ),
    [roles],
  );
  return {
    showShopeeAffiliate,
    roles,
  };
}

interface FilterAdvanceProps {
  assistantList?: AssistantFromSameDeptData[];
  onConfirm: (value: ApiConversation.ConversationListAdvanceParams) => void;
  /** 与列表同源；分类视图由父组件传入 mine/all 的筛选，不传则使用全局 atom */
  filterAdvance?: ConversationFilterAdvance;
}

export interface FilterAdvanceRef {
  clean: () => void;
}

const FilterAdvanceCom = (props: FilterAdvanceProps, ref: Ref<FilterAdvanceRef>) => {
  const { assistantList, onConfirm, filterAdvance } = props;
  const [visible, { toggle: toggleVisible }] = useToggle();
  const { startFilter } = useFilterOptions();
  const { t } = useTranslate();
  const [form] = Form.useForm();
  const isAdminViewWithMultiAssist = useIsAdminViewWithMultiAssist();
  // 美客多接待权限
  const hasMercadoChatPermission = useHasMercadoChatPermission();
  // 只有美客多平台
  const onlyMercadoPlatform = useOnlyMercadoPlatform();

  const { showShopeeAffiliate } = useShowShopeeAffiliate();

  useImperativeHandle(ref, () => {
    return {
      clean: () => {
        form.resetFields();
      },
    };
  });

  // 订单状态
  const orderStatusOptions = useMemo(
    () => [
      {
        label: t('aide.售前'),
        value: 'BEFORE_SALES_STATUS',
      },
      {
        label: t('aide.售中'),
        value: 'DURING_SALES_STATUS',
      },
      {
        label: t('aide.售后'),
        value: 'AFTER_SALES_STATUS',
      },
    ],
    [t],
  );

  useEffect(() => {
    if (visible) {
      form.setFieldsValue(filterAdvance);
    }
  }, [visible, form, filterAdvance]);

  return (
    <>
      <div className="flex justify-center">
        <Button className="m-auto" type="link" size="small" onClick={toggleVisible}>
          {t('conv_filter_advance_title')}
        </Button>
      </div>

      {visible && (
        <Modal
          width={440}
          title={t('conv_filter_advance_title')}
          visible={visible}
          bodyStyle={{ padding: 0 }}
          onCancel={() => {
            form.resetFields();
            toggleVisible();
          }}
          onOk={() => {
            onConfirm(form.getFieldsValue());
            toggleVisible();
          }}
          okText={t('phrase.确定')}
        >
          <div className="px-4 py-2 bg-bg-light">{t('conv_filter_advance_alert_msg')}</div>
          <div className="py-6 px-12">
            <Form form={form} layout="vertical">
              <Form.Item className="!mb-2" label={t('conversation_status')} name="statusFilters">
                <ConversationStatusSelect />
              </Form.Item>
              {hasMercadoChatPermission && (
                <Form.Item className="!mb-2" label={t('会话标签')} name="statusFilters">
                  <ConversationTagSelect />
                </Form.Item>
              )}
              <Form.Item className="!mb-2" label={t('filter.asterisk')} name="asteriskFilters">
                <Select
                  mode="multiple"
                  showArrow
                  showSearch={false}
                  placeholder={t('WhatsApp.请选择')}
                >
                  {startFilter.map((star) => (
                    <Select.Option value={star.value}>
                      {star.icon}
                      {star.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              {!onlyMercadoPlatform && (
                <Form.Item
                  className="!mb-2"
                  label={t('买家售前售中售后状态')}
                  name="salesStatusFilters"
                >
                  <Select
                    mode="multiple"
                    showArrow
                    showSearch={false}
                    clearIcon
                    options={orderStatusOptions}
                    placeholder={t('WhatsApp.请选择')}
                  />
                </Form.Item>
              )}
              {isAdminViewWithMultiAssist && assistantList && (
                <Form.Item className="!mb-2" label={t('department.客服')} name="assistantFilters">
                  <Select
                    mode="multiple"
                    optionFilterProp="children"
                    filterOption={(input, option) => (option?.label ?? '').includes(input)}
                    showArrow
                    showSearch
                    options={assistantList.map((value) => ({
                      label: value.name,
                      value: `${value.id}`,
                    }))}
                    placeholder={t('WhatsApp.请选择')}
                  />
                </Form.Item>
              )}
              <Form.Item noStyle name="excludeShopeeAffiliate" />
              <Form.Item noStyle name="onlyShopeeAffiliate" />
              {showShopeeAffiliate && (
                <Form.Item
                  shouldUpdate
                  dependencies={['excludeShopeeAffiliate, onlyShopeeAffiliate']}
                  className="!mb-2"
                  label={`Shopee ${t('header.会话')}`}
                >
                  {({ getFieldValue, setFieldsValue }) => {
                    return (
                      <Select
                        allowClear
                        placeholder={t('WhatsApp.请选择')}
                        value={
                          getFieldValue('excludeShopeeAffiliate')
                            ? 'excludeShopeeAffiliate'
                            : getFieldValue('onlyShopeeAffiliate')
                            ? 'onlyShopeeAffiliate'
                            : undefined
                        }
                        onChange={(value) =>
                          setFieldsValue({
                            excludeShopeeAffiliate:
                              value === 'excludeShopeeAffiliate' ? true : undefined,
                            onlyShopeeAffiliate: value === 'onlyShopeeAffiliate' ? true : undefined,
                          })
                        }
                        options={[
                          { value: 'excludeShopeeAffiliate', label: `Shopee ${t('买家会话')}` },
                          { value: 'onlyShopeeAffiliate', label: `Shopee ${t('达人会话')}` },
                        ]}
                      />
                    );
                  }}
                </Form.Item>
              )}
            </Form>
          </div>
        </Modal>
      )}
    </>
  );
};

export const FilterAdvance = forwardRef(FilterAdvanceCom);
